import {
    Component,
    ElementRef,
    ViewChild,
    AfterViewInit
} from '@angular/core';
import * as d3 from 'd3';
import { forkJoin, map, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BranchService } from 'src/app/services/branch.service';
import { CategoryService } from 'src/app/services/category.service';
import { MacroPhaseService } from 'src/app/services/macrophase.service';
import { PhaseService } from 'src/app/services/phase.service';
import { CfgService } from 'src/app/services/cfg.service';
import { ToastrService } from 'ngx-toastr';
import { UserSessionService } from '../services/user-session.service';
import { AuthService } from '../auth/auth.service';
import { CountryService } from 'src/app/services/country.service';
import { CompanyService } from '../services/companyService';

interface NodeData {
    name: string;
    type: string;
    code?: string;
    isActive?: boolean;
    isVisible?: boolean;
    children?: NodeData[];
    _children?: NodeData[];
    originalData?: any;
}

interface SearchResult {
    nationIso: string;
    nationName: string;
    type: 'LOB' | 'Categoria' | 'Macrofase' | 'Fase';
    ids: { branchId?: number; categoryId?: number; macroId?: number; phaseId?: number; };
    labels: { branchName?: string; categoryName?: string; macroName?: string; phaseName?: string; };
    pathNames: string[]; // sequenza .name esatta usata nell’albero (per restoreOpenedPath)
}
@Component({
    selector: 'app-cfg',
    templateUrl: './cfg.component.html',
    styleUrls: ['./cfg.component.scss']
})
export class CfgComponent {
    @ViewChild('diagramDiv', { static: true }) diagramRef!: ElementRef;

    branches: any[] = [];
    categories: any[] = [];
    macroPhases: any[] = [];
    phases: any[] = [];

    showModal = false;
    currentNode: NodeData | null = null;
    newNodeLabel = '';
    zoomLevel: number = 0.7;
    selectedPhase: any = null;
    treeData: NodeData = {
        name: 'PREVENTIVATORE',
        type: 'Root',
        children: []
    };
    selectedMacroPhase: any = null;
    selectedCategory: any = null;
    percIncrementoFestivoRif: number = 0;
    percIncrementoNotturnoRif: number = 0;
    percIncrementoTrasfertaRif: number = 0;
    isLoading = false;
    openedNodePath: string[] = [];
    allowedCompanyIds = new Set<number>();
    countries: Array<{ id: string; name: string }> = [];

    // stato popup duplicazione
    confirmDupVisible = false;
    itemToDuplicate: {
        type: 'Macrofase' | 'Categoria' | 'Fase';
        name: string;
        original: any; // <-- solo i dati "puliti"
        dNode: any;    // <-- il nodo D3 separato (può essere circolare)
    } | null = null;

    openConfirmDuplication(
        type: 'Macrofase' | 'Categoria' | 'Fase',
        original: any,
        dNode: any
    ) {
        this.itemToDuplicate = { type, name: original.name, original, dNode };
        this.confirmDupVisible = true;
    }

    onCancelDuplication() {
        this.confirmDupVisible = false;
        this.itemToDuplicate = null;
    }

    onConfirmDuplication() {
        if (!this.itemToDuplicate) return;
        const { type, original, dNode } = this.itemToDuplicate;

        this.confirmDupVisible = false;

        switch (type) {
            case 'Macrofase':
                this.duplicateMacroPhase(original, dNode);
                break;
            case 'Categoria':
                this.duplicateCategory(original, dNode);
                break;
            case 'Fase':
                this.duplicatePhase(original, dNode);
                break;
        }
        this.itemToDuplicate = null;
    }

    private panX = 0;
    private panY = 0;
    private isPanning = false;
    private dragStart = { x: 0, y: 0 };
    private panStart = { x: 0, y: 0 };

    constructor(
        private branchService: BranchService,
        private categoryService: CategoryService,
        private macroPhaseService: MacroPhaseService,
        private phaseService: PhaseService,
        private translate: TranslateService,
        private cfgService: CfgService,
        private authService: AuthService,
        private toastr: ToastrService,
        private userSessionService: UserSessionService,
        private countryService: CountryService,
        private companyService: CompanyService,

    ) {
        this.treeData = {
            name: 'Configuratore',
            type: 'Root',
            children: [] // le nazioni verranno caricate dinamicamente
        };
    }
    // === Ricerca ===
    showSearchModal = false;
    searchQuery = '';
    searchResults: SearchResult[] = [];
    searchLoading = false;

    private highlightPath: string[] | null = null; // per evidenziare il nodo dopo la navigazione



    // In classe
    private textCtx: CanvasRenderingContext2D | null = null;
    private depthOffsets: number[] = []; // posizioni y cumulative per ogni depth

    private colors = {
        bg: '#ffffff',             // sfondo
        panel: '#f5f7fa',
        primary: '#293683',         // attivo (blu brand)
        accent: '#c49a17',
        text: '#1a1a1a',
        textMuted: '#6b7280',

        // Stati derivati dal blu primary
        linkPath: ' #9ca3af',
        inactive: '#9ca3af',        // blu più chiaro → inattivo ma visibile
        invisibleStroke: '#2b2f48ff', // azzurro attenuato → invisibile ma attivo
        inactiveInvisible: '#9ca3af' // azzurrino molto chiaro → inattivo + invisibile
    };




    private leftMax: number[] = [];
    private rightMax: number[] = [];
    private currentUserId: any;





    private getTextWidth(label: string, font = '14px sans-serif'): number {
        if (!this.textCtx) {
            const c = document.createElement('canvas');
            this.textCtx = c.getContext('2d');
        }
        if (!this.textCtx) return label.length * 8; // fallback
        this.textCtx.font = font;
        return this.textCtx.measureText(label).width;
    }


    initCountriesFromVisibilities(): void {
        // 1) prendo le sedi su cui l'utente ha visibilità
        const vis$ = this.userSessionService.getVisibilities().pipe(
            map((res: any) => (res?.UsersCompaniesRel ?? [])
                .map((c: any) => Number(c.id))
                .filter((n: number) => Number.isFinite(n))),
            tap((ids: number[]) => {
                this.allowedCompanyIds = new Set(ids);
                console.log('[CFG] visible company ids:', ids);
            })
        );

        // 2) elenco company<->country
        const companies$ = this.companyService.getSocietiesAndCountries().pipe(
            map((rows: any[]) => Array.isArray(rows) ? rows : []),
            tap(rows => console.log('[CFG] societies+companies:', rows))
        );

        // 3) master countries (per i nomi)
        const countriesMaster$ = this.countryService.index().pipe(
            map((rows: any[]) => Array.isArray(rows) ? rows : []),
            tap(rows => console.log('[CFG] countries master:', rows))
        );

        forkJoin({ visIds: vis$, companies: companies$, master: countriesMaster$ })
            .subscribe({
                next: ({ visIds, companies, master }) => {
                    // filtra i companies per le sedi visibili
                    const isoSet = new Set<string>();
                    for (const row of companies) {
                        const cid = Number(row.companyId);
                        if (this.allowedCompanyIds.has(cid)) {
                            const iso = String(row.companyCountry || row.societyCountry || '').toUpperCase();
                            if (iso) isoSet.add(iso);
                        }
                    }

                    // mappa ISO->Nome
                    const nameByIso = new Map(
                        master.map((c: any) => [String(c.isoCode || '').toUpperCase(), c.name || c.label || c.isoCode])
                    );

                    this.countries = Array.from(isoSet).map(iso => ({
                        id: iso,
                        name: nameByIso.get(iso) || iso
                    }));

                    console.log('[CFG] visible countries:', this.countries);

                    // render dell’albero
                    this.treeData.children = [];
                    if (!this.countries.length) {
                        this.renderTree(); // solo root
                        return;
                    }
                    this.buildTreeData();
                    this.renderTree();
                },
                error: (err) => {
                    console.error('[CFG] initCountriesFromVisibilities error:', err);
                    this.countries = [];
                    this.treeData.children = [];
                    this.renderTree();
                }
            });
    }



    private computeDepthOffsets(root: d3.HierarchyNode<NodeData>) {
        // per ogni depth, quanto spazio massimo a sinistra e a destra del punto (x=0,y=colonna)
        const leftMax: number[] = [];
        const rightMax: number[] = [];
        this.leftMax = leftMax;
        this.rightMax = rightMax;
        // parametri: spazi tra testo e nodo, icone, gap base tra colonne
        const labelPad = 15;         // il tuo x=±10
        const extraIconSpace = 50;   // spazio “di sicurezza” per + e ✎
        const baseGap = 100;          // gap base tra una colonna e la successiva (aumenta se vuoi più aria)

        const isLeafNode = (n: any) => {
            const ch = n.children || n.data.children;
            const hidden = n.data._children;
            return (!ch || ch.length === 0) && (!hidden || hidden.length === 0);
        };

        root.each((node: any) => {
            const d: NodeData = node.data;
            const label = d.name ?? '';
            let w = this.getTextWidth(label, '14px sans-serif');

            const showsPlus = ['LOB', 'Categoria', 'Macrofase'].includes(d.type);
            const showsEdit = ['Fase', 'Macrofase', 'Categoria'].includes(d.type);
            const showsDuplicate = ['Fase', 'Macrofase', 'Categoria'].includes(d.type);
            const anyIcon = showsPlus || showsEdit || showsDuplicate;
            const iconSlack = anyIcon ? extraIconSpace : 0;


            // foglie: testo a destra (x=+10, anchor start) => sporge a destra
            // non-foglie: testo a sinistra (x=-10, anchor end) => sporge a sinistra
            const leaf = isLeafNode(node);
            const depth: number = node.depth;

            if (leaf) {
                // destra = pad + larghezza + eventuali icone
                const protrusionRight = labelPad + w + iconSlack;
                rightMax[depth] = Math.max(rightMax[depth] || 0, protrusionRight);
                leftMax[depth] = Math.max(leftMax[depth] || 0, 0);
            } else {
                // sinistra = pad + larghezza + eventuali icone
                const protrusionLeft = labelPad + w + iconSlack;
                leftMax[depth] = Math.max(leftMax[depth] || 0, protrusionLeft);
                rightMax[depth] = Math.max(rightMax[depth] || 0, 0);
            }
        });

        // ora calcola gli offset cumulativi: offset[0]=0,
        // offset[d] = offset[d-1] + rightMax[d-1] + leftMax[d] + baseGap
        this.depthOffsets = [];
        this.depthOffsets[0] = 0;
        const maxDepth = Math.max(leftMax.length, rightMax.length);
        for (let d = 1; d < maxDepth; d++) {
            const prevRight = rightMax[d - 1] || 0;
            const currLeft = leftMax[d] || 0;
            this.depthOffsets[d] = (this.depthOffsets[d - 1] || 0) + prevRight + currLeft + baseGap;
        }
    }

    private uniqueName(baseName: string, siblings: { name: string }[]): string {
        const norm = (s: string) => (s || '').toLowerCase();
        const exist = new Set((siblings || []).map(s => norm(s.name)));
        const base1 = `${baseName} (copia)`;
        if (!exist.has(norm(base1))) return base1;
        let i = 2;
        while (exist.has(norm(`${baseName} (copia ${i})`))) i++;
        return `${baseName} (copia ${i})`;
    }

    // se non hai già un generator per le categorie
    private generateUniqueCategoryCode(): string {
        const used = new Set(this.categories.map((c: any) => c.code).filter(Boolean));
        let i = 1;
        while (used.has(`CAT${i}`)) i++;
        return `CAT${i}`;
    }

    // --- Helpers comuni ---
    private stripCommon(o: any) {
        const {
            id, createdAt, updatedAt, // tolgo chiavi server-managed
            BranchesCategoriesRel, CategoriesMacroPhasesRel, MacroPhasesPhasesRel, // tolgo rel annidate
            ...rest
        } = o || {};
        return rest;
    }

    // Ricava il path attuale per non perderlo dopo il refresh
    private rememberPathBefore(actionNode: any) {
        this.saveOpenedPath(actionNode); // usa già la tua funzione
    }

    // --- Categoria ---
    private duplicateCategory(original: any, dNode?: any) {
        // ricavo branchId dal record o dal parent (LOB)
        const branchId = original.branchId ?? dNode?.parent?.data?.originalData?.id;
        const siblings = this.categories.filter((c: any) => c.branchId === branchId);

        const base = this.stripCommon(original);
        const payload = this.withUpdatedBy({
            ...base,
            id: undefined,
            branchId,
            code: this.generateUniqueCategoryCode(),
            name: this.uniqueName(original.name, siblings),
        });

        // mantieni il ramo aperto
        this.rememberPathBefore(dNode);

        this.isLoading = true;
        this.cfgService.createCategory(payload).subscribe({
            next: saved => {
                // rimani nello stesso ramo e apri fino alla nuova categoria
                this.restoreAndUpdateTreeAfterSave(saved?.id, 'Categoria');
                this.toastr.success(this.translate.instant('LABEL.OK_CFG'), 'Categoria duplicata');
            },
            error: err => {
                console.error('Duplica categoria KO', err);
                this.isLoading = false;
                this.toastr.error(this.translate.instant('LABEL.KO_CFG'), 'Errore duplicazione categoria');
            }
        });
    }
    /** Estrae dal nodo D3 (macrofase) i nomi per ricostruire la breadcrumb */
    private getNamesContextForMacroFromDNode(dNode: any): {
        nationIso: string;
        branchName: string;
        categoryName: string;
        macroId: number;
        macroName: string;
    } | null {
        if (!dNode) return null;

        const macroNode = dNode;                 // il nodo cliccato è proprio la macrofase
        const catNode = macroNode?.parent;
        const lobNode = catNode?.parent;

        const macro = macroNode?.data?.originalData;
        const cat = catNode?.data?.originalData;
        const br = lobNode?.data?.originalData;

        // ✅ prendi il country direttamente dai dati del nodo/parent
        let nationIso =
            (br?.codCountry ?? cat?.codCountry ?? macro?.codCountry ?? '')
                .toString()
                .toUpperCase();

        // fallback se proprio manca
        if (!nationIso) {
            nationIso = this.getNationCodeFromLabel(
                this.openedNodePath.find(n => typeof n === 'string' && n.startsWith('Nazione: '))
            );
        }

        if (!macro?.id || !macro?.name || !cat?.name || !br?.name || !nationIso) return null;

        return {
            nationIso,
            branchName: (lobNode?.data?.name || br.name), // label esatta usata nell’albero
            categoryName: cat.name,
            macroId: macro.id,
            macroName: macro.name
        };
    }


    // --- Macrofase ---
    private duplicateMacroPhase(original: any, dNode?: any) {
        const categoryId = original.categoryId ?? dNode?.parent?.data?.originalData?.id;

        const siblings = this.macroPhases.filter((m: any) => m.categoryId === categoryId);
        const base = this.stripCommon(original);
        const payload = this.withUpdatedBy({
            ...base,
            id: undefined,
            categoryId,
            code: this.generateUniqueMacroPhaseCode?.() ?? base.code,
            name: this.uniqueName(original.name, siblings),
        });

        // 🔎 contesto nomi dalla macrofase selezionata
        const namesCtx = this.getNamesContextForMacroFromDNode(dNode);
        this.rememberPathBefore(dNode);

        this.isLoading = true;
        this.cfgService.createMacroPhase(payload).subscribe({
            next: saved => {
                console.log('[CFG] Duplica macrofase OK → ripristino path per:', {
                    macroId: saved?.id, macroName: saved?.name, namesCtx
                });

                // riapri fino alla MACRO appena creata usando nomi + guard macroId
                this.restoreAndUpdateTreeAfterSave(
                    saved?.id,
                    'Macrofase',
                    namesCtx
                );

                this.toastr.success(this.translate.instant('LABEL.OK_CFG'), 'Macrofase duplicata');
            },
            error: err => {
                console.error('Duplica macrofase KO', err);
                this.isLoading = false;
                this.toastr.error(this.translate.instant('LABEL.KO_CFG'), 'Errore duplicazione macrofase');
            }
        });
    }

    private DEBUG = true;
    private dbg(label: string, ...args: any[]) {
        if (!this.DEBUG) return;
        // usa groupCollapsed per non intasare
        // eslint-disable-next-line no-console
        console.groupCollapsed(`[CFG][DBG] ${label}`);
        // eslint-disable-next-line no-console
        console.log(...args);
        // eslint-disable-next-line no-console
        console.groupEnd();
    }

    private duplicatePhase(original: any, dNode?: any) {
        const macroPhaseId = original.macroPhaseId ?? dNode?.parent?.data?.originalData?.id;
        const siblings = this.phases.filter((p: any) => p.macroPhaseId === macroPhaseId);
        const base = this.stripCommon(original);

        const payload = this.withUpdatedBy({
            ...base,
            id: undefined,
            macroPhaseId,
            code: this.generateUniquePhaseCode(),
            name: this.uniqueName(original.name, siblings),
        });

        // ricava contesto nomi dal nodo cliccato (per path by name + guard macro)
        const namesCtx = this.getNamesContextFromDNode(dNode);

        this.rememberPathBefore(dNode);
        this.isLoading = true;

        this.cfgService.createPhase(payload).subscribe({
            next: saved => {
                // dopo refresh, cerco per NOME sotto la MACRO giusta
                this.restoreAndUpdateTreeAfterSave(
                    saved?.id,       // utile come fallback
                    'Fase',
                    namesCtx,        // { nationIso, branchName, categoryName, macroId, macroName }
                    saved?.name      // "Fase: <name>"
                );

                this.toastr.success(this.translate.instant('LABEL.OK_CFG'), 'Fase duplicata');
            },
            error: err => {
                console.error('Duplica fase KO', err);
                this.isLoading = false;
                this.toastr.error(this.translate.instant('LABEL.KO_CFG'), 'Errore duplicazione fase');
            }
        });
    }

    private findPathToNodeByIdAndType(id: number, type: string): string[] | null {
        const path: string[] = [];

        const dfs = (node: any): boolean => {
            const kids = (node.children || node._children || []);
            // match del nodo corrente
            if (node.originalData?.id === id && node.type === type) {
                path.push(node.name);
                return true;
            }
            // scendi tra i figli
            for (const child of kids) {
                if (dfs(child)) {
                    path.unshift(node.name);
                    return true;
                }
            }
            return false;
        };

        // parte dalla root dei dati (treeData)
        if (dfs(this.treeData as any)) return path;
        return null;
    }


    ngAfterViewInit(): void {
        this.initCountriesFromVisibilities();

        // this.renderTree();    // mostra i nodi iniziali
        this.userSessionService
            .getUser(this.authService.user().email)
            .subscribe({
                next: (user: any) => {
                    // console.log('user:', user)
                    this.currentUserId = user[0].id;
                    // console.log('userId', this.currentUserId);
                },
                error: err => {
                    console.error('Errore nel recupero utente', err);
                    this.currentUserId = null;
                }
            });
    }



    private filterByCountry<T extends { codCountry?: string }>(list: T[], codCountry: string): T[] {
        return list.filter(item => item.codCountry === codCountry);
    }


    updateBranchesAndSoOn(nationCode: string, nodeRef?: any) {
        this.isLoading = true;

        forkJoin({
            branches: this.branchService.getAllBranches(),
            categories: this.categoryService.getAllCategories(),
            macroPhases: this.macroPhaseService.getAllMacroPhases(),
            phases: this.phaseService.getAllPhases(),
        }).subscribe({
            next: res => {
                this.branches = this.filterByCountry(res.branches, nationCode);
                this.categories = this.filterByCountry(res.categories, nationCode);
                this.macroPhases = this.filterByCountry(res.macroPhases, nationCode);
                this.phases = this.filterByCountry(res.phases, nationCode);

                if (nodeRef) {
                    nodeRef.data.children = this.buildChildrenForNation(nationCode);
                    delete nodeRef.data._children; // ✅ assicura l’espansione

                }

                this.restoreOpenedPath();
                this.renderTree();
                this.isLoading = false;
            },
            error: () => this.isLoading = false
        });
    }


    buildTreeData() {
        this.treeData.children = (this.countries || []).map(c => ({
            name: `Nazione: ${c.name}`,
            type: 'Nazione',
            code: c.id,          // ISO (IT/FR/ES/...)
            _children: []        // collassate inizialmente
        }));
    }




    toggle(d: any): void {
        const root = d3.hierarchy(this.treeData);

        if (d.children) {
            // Nodo aperto → chiudilo
            d.data._children = d.children.map((child: any) => child.data);
            delete d.data.children;
            this.renderTree();
        } else {
            this.isLoading = true;

            if (d.data.type === 'Nazione') {
                this.collapseOtherNodes(root, 'Nazione', d);
                const codCountry = d.data.code;

                this.saveOpenedPath(d); // Salva path corrente

                // 👇 Chiamata dati dinamica al click sulla nazione
                this.updateBranchesAndSoOn(codCountry, d);
            } else {
                this.collapseOtherNodes(root, d.data.type, d);

                if (d.data._children) {
                    this.saveOpenedPath(d);
                    d.data.children = d.data._children;
                    delete d.data._children;
                    this.computeDepthOffsets(d);


                    this.restoreOpenedPath();
                    this.renderTree();
                }

                this.isLoading = false;
            }
        }
    }

    private buildPathByNamesAndMacroGuard(ctx: {
        nationIso: string;          // es. "IT"
        branchName: string;         // es. "BUILDINGS"
        categoryName: string;       // es. "Categoria X"
        macroId: number;            // id macrofase "padre"
        macroName: string;          // es. "Macrofase Y"
        phaseName?: string;         // es. "Fase Z" (opzionale)
    }): string[] | null {
        // 1) Etichetta "Nazione: <nome>"
        const nationObj = this.countries.find(c => c.id === (ctx.nationIso || '').toUpperCase());
        const nationLabel = nationObj
            ? `Nazione: ${String(nationObj.name || '').trim()}`
            : (this.openedNodePath.find(x => x.startsWith('Nazione: ')) || null);

        if (!nationLabel) {
            console.log('[CFG] buildPath -> Nazione non risolta per', ctx);
            return null;
        }

        let curr: any = this.treeData;

        // Helper: step su label (con eventuale predicate) + log se fallisce
        const step = (label: string, predicate?: (n: any) => boolean): any => {
            const kids = (curr.children || curr._children || []);
            const hit = kids.find((c: any) =>
                (String(c.name || '').trim() === String(label || '').trim()) &&
                (predicate ? predicate(c) : true)
            );

            if (!hit) {
                const avail = kids.map((c: any) => String(c.name || '').trim());
                if (label === ctx.branchName) {
                    console.log('[CFG] LOB NON trovata. Cercavo:', label, 'Disponibili:', avail);
                } else if (label.startsWith('Categoria:')) {
                    console.log('[CFG] Categoria NON trovata. Cercavo:', label, 'Disponibili:', avail);
                } else if (label.startsWith('Macrofase:')) {
                    console.log('[CFG] Macrofase NON trovata (per label/predicate). Cercavo:', label, 'Disponibili:', avail);
                } else if (label.startsWith('Fase:')) {
                    console.log('[CFG] Fase NON trovata. Cercavo:', label, 'Disponibili:', avail);
                } else {
                    console.log('[CFG] Nodo NON trovato. Cercavo:', label, 'Disponibili:', avail);
                }
                return null;
            }

            // Se il target ha _children, espandi per proseguire la discesa
            if (hit._children && !hit.children) { hit.children = hit._children; delete hit._children; }
            curr = hit;
            return hit;
        };

        // --- Nazione ---
        if (!step(nationLabel)) return null;
        if (!curr.children && curr._children) { curr.children = curr._children; delete curr._children; }

        // --- LOB (nome "puro", senza prefisso) ---
        if (!step(String(ctx.branchName || '').trim())) return null;

        // Log categorie disponibili sotto la LOB
        {
            const kids = (curr.children || curr._children || []);
            console.log('[CFG] LOB OK:', ctx.branchName, 'Categorie disponibili:', kids.map((c: any) => c.name));
        }

        // --- Categoria ---
        const catLabel = `Categoria: ${String(ctx.categoryName || '').trim()}`;
        console.log('[CFG] Step Categoria -> cerco:', catLabel);
        if (!step(catLabel)) return null;

        // Log macrofasi disponibili subito dopo l’ingresso in categoria
        {
            const kids = (curr.children || curr._children || []);
            console.log(
                '[CFG] Categoria OK:', catLabel,
                'Macrofasi disponibili:',
                kids.map((m: any) => ({ id: m.id, name: m.name }))
            );
        }

        // --- Macrofase (match sia per label che per id col predicate) ---
        const macroLabel = `Macrofase: ${String(ctx.macroName || '').trim()}`;
        console.log('[CFG] Step Macrofase -> cerco:', macroLabel, 'macroId atteso:', Number(ctx.macroId));

        // Assicurati che i figli siano espansi prima del match
        if (!curr.children && curr._children) { curr.children = curr._children; delete curr._children; }

        const macroHit = step(macroLabel, (n: any) => Number(n.id) === Number(ctx.macroId));
        if (!macroHit) {
            const avail = (curr.children || curr._children || []).map((m: any) => ({ id: m.id, name: m.name }));
            console.log('[CFG] Macrofase NON trovata (dopo predicate). Cercavo label/id:', macroLabel, Number(ctx.macroId), 'Disponibili:', avail);
            return null;
        }

        // --- Fase (opzionale) ---
        if (ctx.phaseName) {
            const phaseLabel = `Fase: ${String(ctx.phaseName || '').trim()}`;
            console.log('[CFG] Step Fase -> cerco:', phaseLabel);

            if (!curr.children && curr._children) { curr.children = curr._children; delete curr._children; }

            if (!step(phaseLabel)) {
                const avail = (curr.children || curr._children || []).map((p: any) => ({ id: p.id, name: p.name }));
                console.log('[CFG] Fase NON trovata. Cercavo:', phaseLabel, 'Disponibili:', avail);
                return null;
            }
        }

        // --- Ricostruisci path dal nodo corrente ---
        const path: string[] = [];
        let it = curr;
        while (it) {
            if (typeof it.name === 'string') path.unshift(it.name);
            it = it.parent;
        }

        console.log('[CFG] Path ricostruita:', path);
        return path;
    }


    /** Estrae dal nodo D3 (fase duplicata) i nomi per ricostruire la breadcrumb */
    private getNamesContextFromDNode(dNode: any): {
        nationIso: string;
        branchName: string;
        categoryName: string;
        macroId: number;
        macroName: string;
    } | null {
        if (!dNode) return null;
        const macroNode = dNode.parent;
        const catNode = macroNode?.parent;
        const lobNode = catNode?.parent;

        const macro = macroNode?.data?.originalData;
        const cat = catNode?.data?.originalData;
        const br = lobNode?.data?.originalData;

        // tenta di ricavare l'ISO della nazione dalla path aperta
        const nationIso = this.getNationCodeFromLabel(this.openedNodePath.find(n => n.startsWith('Nazione: ')));

        if (!macro?.id || !macro?.name || !cat?.name || !br?.name || !nationIso) return null;

        return {
            nationIso: String(nationIso).toUpperCase(),
            branchName: lobNode?.data?.name || br.name,
            categoryName: cat.name,
            macroId: macro.id,
            macroName: macro.name
        };
    }



    buildChildrenForNation(nationCode: string): NodeData[] {
        const isTrue = (v: any) => v === true;

        const branches = this.branches
            .filter(b => b.codCountry === nationCode);
        return branches.map(branch => {
            const branchCategories = (branch.BranchesCategoriesRel || [])
                .map((rel: any) => {
                    const catId = rel.BranchesCategories?.CategoryId;
                    const cat = this.categories.find(c => c.id === catId);
                    if (!cat) return null;

                    // Evita errore se la categoria non ha macrofasi
                    if (!Array.isArray(cat.CategoriesMacroPhasesRel)) {
                        cat.CategoriesMacroPhasesRel = [];
                    }

                    return cat;
                })
                .filter(Boolean);

            const categoryNodes = branchCategories.map((cat: any) => {
                const macroNodes = cat.CategoriesMacroPhasesRel.map((rel: any) => {
                    const macroId = rel.CategoriesMacroPhases?.MacroPhaseId || rel.id;
                    return this.macroPhases.find(m => m.id === macroId);
                }).filter(Boolean);

                const macrofaseChildren = macroNodes.map((macro: any) => {
                    const phaseNodes = Array.isArray(macro.MacroPhasesPhasesRel)
                        ? macro.MacroPhasesPhasesRel
                            .map((rel: any) => this.phases.find(p => p.id === rel.id))
                            .filter(Boolean)
                        : [];

                    return {
                        name: `Macrofase: ${macro.name}`,
                        type: 'Macrofase',
                        id: macro.id,
                        isActive: macro.isActive,
                        isVisible: macro.isVisible,
                        originalData: macro, // 👈 AGGIUNTO
                        _children: phaseNodes.map((p: any) => ({
                            name: `Fase: ${p.name}`,
                            type: 'Fase',
                            id: p.id,
                            isActive: p.isActive,
                            isVisible: p.isVisible,
                            originalData: p // 👈 AGGIUNTO
                        }))
                    };
                });

                const categoryName = cat.testDate 
                    ? `Categoria: ${cat.name} - ${cat.testDate}`
                    : `Categoria: ${cat.name}`;

                return {
                    name: categoryName,
                    type: 'Categoria',
                    id: cat.id,
                    isActive: cat.isActive,
                    isVisible: cat.isVisible,
                    _children: macrofaseChildren,
                    originalData: cat  // ✅ aggiunto oggetto completo
                };
            });


            return {
                name: branch.name,
                type: 'LOB',
                id: branch.id,
                isActive: branch.isActive,
                isVisible: branch.isVisible,
                _children: categoryNodes,
                originalData: branch // 👈 AGGIUNTO QUI
            };

        });
    }





    collapseOtherNodes(root: any, type: string, exceptNode: any): void {
        root.each((node: any) => {
            if (node !== exceptNode && node.data.type === type && node.children) {
                node.data._children = node.children.map((c: any) => c.data);
                delete node.data.children;
            }
        });
    }

    renderTree(): void {
        function isLeafNode(d: d3.HierarchyPointNode<NodeData>) {
            const children = d.children || d.data.children;
            const hiddenChildren = d.data._children;
            return (!children || children.length === 0) &&
                (!hiddenChildren || hiddenChildren.length === 0);
        }

        d3.select(this.diagramRef.nativeElement).select('svg').remove();

        const margin = { top: 100, right: 120, bottom: 40, left: 100 };
        const padding = 40;

        const svg = d3.select(this.diagramRef.nativeElement)
            .append('svg')
            .style('background', this.colors.bg);

        // --- glow per highlight ---
        const defs = svg.append('defs');
        const glow = defs.append('filter')
            .attr('id', 'node-glow')
            .attr('x', '-50%').attr('y', '-50%')
            .attr('width', '200%').attr('height', '200%');

        glow.append('feGaussianBlur')
            .attr('stdDeviation', '3')
            .attr('result', 'coloredBlur');

        const feMerge = glow.append('feMerge');
        feMerge.append('feMergeNode').attr('in', 'coloredBlur');
        feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

        const root = d3.hierarchy(this.treeData);

        const treeLayout = d3.tree<NodeData>().nodeSize([120, 200]);
        const tree = treeLayout(root);
        const nodes = tree.descendants();
        // Personalizzazione della distanza Y in base al tipo
        this.computeDepthOffsets(tree);

        // ➋ Forza la y di ogni nodo usando le colonne calcolate
        nodes.forEach(n => {
            n.y = this.depthOffsets[n.depth];
        })

        const xExtent = d3.extent(nodes, d => d.x) as [number, number];
        const yExtent = d3.extent(nodes, d => d.y) as [number, number];

        const diagramWidth = yExtent[1] - yExtent[0] + margin.left + margin.right + padding * 2;
        const diagramHeight = xExtent[1] - xExtent[0] + margin.top + margin.bottom + padding * 2;

        svg.attr('width', diagramWidth).attr('height', diagramHeight);

        const offsetY = margin.top + padding - xExtent[0];
        const baseX = margin.left + padding;
        const baseY = offsetY;

        const container = svg.append('g'); // niente transform qui

        const applyViewportTransform = () => {
            container.attr(
                'transform',
                `translate(${baseX + this.panX},${baseY + this.panY}) scale(${this.zoomLevel})`
            );
        };

        applyViewportTransform();
        svg
            .style('cursor', this.isPanning ? 'grabbing' : 'grab')
            .call(
                d3.drag<SVGSVGElement, unknown>()
                    .on('start', (event) => {
                        this.isPanning = true;
                        svg.style('cursor', 'grabbing');
                        this.dragStart = { x: event.x, y: event.y };
                        this.panStart = { x: this.panX, y: this.panY };
                    })
                    .on('drag', (event) => {
                        this.panX = this.panStart.x + (event.x - this.dragStart.x);
                        this.panY = this.panStart.y + (event.y - this.dragStart.y);
                        applyViewportTransform(); // aggiorna al volo senza rifare renderTree
                    })
                    .on('end', () => {
                        this.isPanning = false;
                        svg.style('cursor', 'grab');
                    })
            );


        const linkGroup = container.append('g').attr('class', 'links');
        const nodeGroup = container.append('g').attr('class', 'nodes');

        const corridor = 16; // margine extra oltre al testo (tweakabile)

        const linkPath = (d: d3.HierarchyPointLink<NodeData>) => {
            const sy = d.source.y + (this.rightMax[d.source.depth] || 0) + corridor; // esci oltre il testo del padre
            const ty = d.target.y - (this.leftMax[d.target.depth] || 0) - corridor; // entra prima del testo del figlio

            return `
    M${d.source.y},${d.source.x}
    H${sy}
    C${(sy + ty) / 2},${d.source.x}
     ${(sy + ty) / 2},${d.target.x}
     ${ty},${d.target.x}
    H${d.target.y}
  `;
        };
        const isActive = (d: any) => d.data.isActive !== false;
        const isVisible = (d: any) => d.data.isVisible !== false;

        const linkColor = (d: any) => {
            const a = d.data.isActive !== false;
            const v = d.data.isVisible !== false;
            return (a && v) ? this.colors.primary : this.colors.inactive;
        };


        const strokeColor = (d: any) => {
            // opzionale: nazione sempre primaria
            if (d.data?.type === 'Nazione') return this.colors.primary;

            const a = isActive(d);
            const v = isVisible(d);

            if (!a && !v) return this.colors.inactiveInvisible; // entrambi false
            if (!a && v) return this.colors.inactive;        // solo inactive
            if (a && !v) return this.colors.invisibleStroke; // solo invisible
            return this.colors.primary;                       // attivo+visibile
        };

        const fillColor = (d: any) => {
            // opzionale: nazione sempre primaria
            if (d.data?.type === 'Nazione') return this.colors.primary;

            const a = isActive(d);
            const v = isVisible(d);

            if (!a && !v) return this.colors.bg;        // entrambi false -> bianco
            if (!a && v) return this.colors.inactive;  // inactive -> grigio pieno
            if (a && !v) return this.colors.bg;        // invisible -> bianco
            return this.colors.primary;                 // attivo+visibile -> pieno
        };

        linkGroup.selectAll('path.link')
            .data(tree.links())
            .enter()
            .append('path')
            .attr('class', 'link')
            .attr('d', linkPath)
            .attr('stroke', d => linkColor(d.target)) // usa target per lo stato del figlio
            .attr('stroke-width', 1.5)
            .attr('fill', 'none');

        const node = nodeGroup.selectAll('g.node')
            .data(tree.descendants())
            .enter()
            .append('g')
            .attr('transform', d => `translate(${d.y},${d.x})`)
            .attr('class', 'node');

        // hit-area: disco pieno invisibile (ma cliccabile) — NON solo stroke
        node.append('circle')
            .attr('class', 'hit-area')
            .attr('r', 22)                           // più grande del cerchio visivo
            .attr('fill', '#ffffff')                 // un fill "dipinto"…
            .attr('fill-opacity', 0.0001)            // …ma praticamente invisibile
            .style('pointer-events', 'all')          // tutta l’area del disco è cliccabile
            .style('cursor', d => (d.children || d.data._children ? 'pointer' : 'default'))
            .on('click', (event, d) => {
                if (this.isPanning) return;
                this.toggle(d);
            });

        // cerchio visivo (immutato e senza eventi)
        node.append('circle')
            .attr('r', d => (d.data as any).__flash ? 8 : 6)
            .attr('fill', d => (d.data as any).__flash ? this.colors.bg : fillColor(d))
            .attr('stroke', d => (d.data as any).__flash ? this.colors.accent : strokeColor(d))
            .attr('stroke-width', d => (d.data as any).__flash ? 4 : 2)
            .attr('filter', d => (d.data as any).__flash ? 'url(#node-glow)' : null)
            .style('pointer-events', 'none');
        // il visivo non cattura eventi





        node.append('text')
            .text(d => d.data.name)
            .attr('x', d => isLeafNode(d) ? 10 : -10)
            .attr('y', -10)
            .attr('fill', d => this.colors.text)
            .style('font-size', '14px')
            .style('font-family', 'sans-serif')
            .style('text-anchor', d => isLeafNode(d) ? 'start' : 'end');



        node.raise();
        // Bottone "+"
        const plusNodes = node
            .filter(d => ['LOB', 'Categoria', 'Macrofase'].includes(d.data.type))
            .append('text')
            .text('+')
            .attr('class', 'plus-button')
            .attr('fill', this.colors.accent)
            .style('cursor', 'pointer')
            .attr('y', -10) // manterrai questo per l'allineamento verticale
            .style('font-size', '18px')
            .style('font-family', 'sans-serif');


        const editIcons = node
            .filter(d => ['Fase', 'Macrofase', 'Categoria'].includes(d.data.type))
            .append('text')
            .text('✎') // simbolo matita
            .attr('class', 'edit-icon')
            .attr('fill', this.colors.accent)
            .style('cursor', 'pointer')
            .style('font-size', '16px')
            .style('font-family', 'sans-serif');

        const duplicateIcons = node
            .filter(d => ['Fase', 'Macrofase', 'Categoria'].includes(d.data.type))
            .append('text')
            .text('⧉')
            .attr('class', 'duplicate-icon')
            .attr('fill', this.colors.accent)
            .style('cursor', 'pointer')
            .style('font-size', '16px')
            .style('font-family', 'sans-serif');


        // Posizionamento "+" accanto al testo
        const self = this;

        node.each(function (d) {
            const g = d3.select(this);
            const label = g.select<SVGTextElement>('text:not(.plus-button):not(.edit-icon)');
            const isLeaf = isLeafNode(d);

            const plus = g.select<SVGTextElement>('text.plus-button');
            const edit = g.select<SVGTextElement>('text.edit-icon');

            if (!label.empty()) {
                const bbox = label.node()!.getBBox();
                const offsetX = +label.attr('x') || 0;
                const offsetY = +label.attr('y') || 0;
                const dup = g.select<SVGTextElement>('text.duplicate-icon');

                if (!dup.empty()) {
                    // dentro node.each(function (d) { ... })
                    dup
                        .attr('x', isLeaf ? offsetX + bbox.width + 65 : offsetX - bbox.width - 75)
                        .attr('y', offsetY)
                        .style('text-anchor', isLeaf ? 'start' : 'end')
                        .on('click', (event, d: any) => {
                            event.stopPropagation();
                            if (self.isPanning) return;

                            const original = d.data.originalData;
                            if (!original) return;

                            self.openConfirmDuplication(
                                d.data.type as 'Fase' | 'Macrofase' | 'Categoria',
                                original,
                                d
                            );
                        })
                        .append('title')
                        .text('Duplica');

                }


                if (!plus.empty()) {
                    plus
                        .attr('x', isLeaf ? offsetX + bbox.width + 5 : offsetX - bbox.width - 15)
                        .attr('y', offsetY)
                        .style('text-anchor', isLeaf ? 'start' : 'end')
                        .on('click', (_, d: any) => {
                            const original = d.data.originalData;

                            if (!original) return;

                            if (d.data.type === 'Macrofase') {
                                self.openPhaseDialog({ ...original }); // crea fase nella macrofase

                            } else if (d.data.type === 'Categoria') {
                                self.openMacroPhaseDialog(undefined, false, original); // crea macrofase nella categoria

                            } else if (d.data.type === 'LOB') {
                                self.openCategoryDialog(undefined, original); // crea categoria nella LOB

                            } else {
                                self.currentNode = d.data;
                                self.showModal = true;
                            }
                        });
                }

                if (!edit.empty()) {
                    edit
                        .attr('x', isLeaf ? offsetX + bbox.width + 25 : offsetX - bbox.width - 45)
                        .attr('y', offsetY)
                        .style('text-anchor', isLeaf ? 'start' : 'end')
                        .on('click', (_, d: any) => {
                            const original = d.data.originalData;

                            if (!original) return;

                            if (d.data.type === 'Fase') {
                                self.selectedPhase = { ...original };

                            } else if (d.data.type === 'Macrofase') {
                                self.openMacroPhaseDialog({ ...original }, true); // modifica macrofase

                            } else if (d.data.type === 'Categoria') {
                                self.openCategoryDialog({ ...original }); // modifica categoria
                            }
                        });
                }
            }
        });



    }

    getNextLevelType(currentType: string): string {
        switch (currentType) {
            case 'LOB':
                return 'Categoria';
            case 'Categoria':
                return 'Macrofase';
            case 'Macrofase':
                return 'Fase';
            default:
                return 'Nodo';
        }
    }

    confirmAddNode(): void {
        if (!this.currentNode || !this.newNodeLabel.trim()) return;

        const type = this.getNextLevelType(this.currentNode.type);
        const newNode: NodeData = {
            name: `${type}: ${this.newNodeLabel.trim()}`,
            type
        };

        // Se il nodo è collassato (ha _children), recupera i figli salvati
        const existingChildren = this.currentNode.children ?? this.currentNode._children ?? [];

        // Aggiungi il nuovo nodo
        const updatedChildren = [...existingChildren, newNode];

        // Espandi il nodo forzando `children` e rimuovendo `_children`
        this.currentNode.children = updatedChildren;
        delete this.currentNode._children;

        this.closeModal();
        this.renderTree();
    }


    closeModal(): void {
        this.showModal = false;
        this.newNodeLabel = '';
        this.currentNode = null;
    }

    zoomIn() {
        this.zoomLevel += 0.1;
        this.renderTree();
    }

    zoomOut() {
        this.zoomLevel = Math.max(0.1, this.zoomLevel - 0.1);
        this.renderTree();
    }

    private withUpdatedBy<T extends object>(payload: T): T & { updatedBy: number | null } {
        return { ...payload, updatedBy: this.currentUserId };
    }


    openPhaseDialog(data?: any, isEdit = false): void {

        if (isEdit && data) {
            this.selectedPhase = { ...data };
            this.currentNode = this.findNodeById(data.id);

        } else {
            this.selectedPhase = {
                codeOldSystem: '',
                name: '',
                desL: '',
                nWorkers: '',
                qProdOraria: '',
                cMater: '',
                FixedPrice: null,
                UnitMeasureIdId: null,
                position: '',
                isActive: true,
                isVisible: true,
                isPercAmmaloramento: false,
                isAdditionalMaterialCost: false,
                isPreselected: false,
                isOtherCost: false,
                isOpereSpeciali: false,
                isMandatory: false,
                isFixedPrice: false,
                codLanguage: data?.codLanguage,
                codCountry: data?.codCountry,
                macroPhaseId: data?.id ?? null, // utile se è passata una macrofase
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            this.currentNode = this.findNodeById(data.id);
        }
    }

    onPhaseDialogClose(): void {
        this.selectedPhase = null;
    }

    onPhaseDialogSave(data: any): void {
        // console.log('Fase salvata:', data);
        // Qui potrai chiamare un servizio per il salvataggio
        this.selectedPhase = null;
        if (!data.name || data.qProdOraria == null || data.cMater == null || data.position == null) {
            alert('Compila tutti i campi obbligatori della fase.');
            return;
        }
        this.renderTree();
    }


    onPhaseSave(data: any): void {
        if (!data.desL || data.position == null) {
            this.toastr.warning(
                this.translate.instant('LABEL.CampiObbligatori'),
                this.translate.instant('LABEL.Attenzione')
            );
            return;
        }

        const isEdit = !!data.id;
        const payload = this.withUpdatedBy(data);

        this.isLoading = true;

        const save$ = isEdit
            ? this.cfgService.updatePhase(payload.id, payload)
            : this.cfgService.createPhase(payload);

        save$.subscribe({
            next: (saved) => {
                // ID certo della fase (sia create che update)
                const targetId = saved?.id ?? payload.id;

                this.selectedPhase = null;

                // Riapre il ramo fino alla fase
                this.restoreAndUpdateTreeAfterSave(targetId, 'Fase');

                this.toastr.success(
                    this.translate.instant('LABEL.OK_CFG'),
                    this.translate.instant('LABEL.FaseSalvataConSuccesso')
                );
            },
            error: (err) => {
                console.error('Errore salvataggio fase:', err);
                this.toastr.error(
                    this.translate.instant('LABEL.ErroreSalvataggio'),
                    this.translate.instant('LABEL.Errore')
                );
                this.isLoading = false;
            }
        });
    }






    openMacroPhaseDialog(data?: any, isEdit = false, category?: any): void {
        // console.log('category', category)
        const now = new Date().toISOString();


        if (isEdit && data) {
            this.selectedMacroPhase = { ...data };
            this.currentNode = this.findNodeById(data.id);
        } else {
            this.selectedMacroPhase = {
                name: '',
                position: '',
                idWorkType: null,
                isFreeDatas: false,
                isMultiService: false,
                isActive: true,
                isVisible: true,
                codCountry: category?.codCountry,
                codLanguage: category?.codLanguage,
                categoryId: category?.id ?? null,
                createdAt: now,
                updatedAt: now
            };
            this.currentNode = this.findNodeById(data.id);

        }
    }


    onMacroPhaseDialogClose(): void {
        this.selectedMacroPhase = null;
    }

    onMacroPhaseSave(data: any): void {
        if (!data.name || data.position == null) {
            this.toastr.error(
                this.translate.instant('LABEL.KO_CFG'),
                this.translate.instant('LABEL.CampiObbligatori')
            );
            return;
        }

        const isEdit = !!data.id;
        const payload = this.withUpdatedBy(data);
        this.isLoading = true;

        const save$ = isEdit
            ? this.cfgService.updateMacroPhase(payload.id, payload)
            : this.cfgService.createMacroPhase(payload);

        save$.subscribe({
            next: (saved) => {
                // ID certo della macrofase (sia create che update)
                const targetId = saved?.id ?? payload.id;

                // prova a ricavare il contesto nomi dal nodo D3 della macrofase (se già presente in albero)
                let namesCtx = null;
                const macroNode = this.findNodeByIdAndType(targetId, 'Macrofase');
                if (macroNode) {
                    namesCtx = this.getNamesContextForMacroFromDNode(macroNode);
                } else if (this.currentNode) {
                    // fallback: se stavi operando da un nodo macrofase/categoria aperto
                    namesCtx = this.getNamesContextForMacroFromDNode(this.currentNode);
                }

                console.log('[CFG] Save macrofase OK → ripristino path per:', {
                    targetId, savedName: saved?.name ?? payload?.name, namesCtx
                });

                this.selectedMacroPhase = null;

                // Riapri il ramo fino alla macrofase appena creata/modificata usando nomi + guard id
                this.restoreAndUpdateTreeAfterSave(targetId, 'Macrofase', namesCtx);

                this.toastr.success(
                    this.translate.instant('LABEL.OK_CFG'),
                    this.translate.instant('LABEL.MacrofaseSalvataConSuccesso')
                );
            },
            error: () => {
                this.isLoading = false;
                this.toastr.error(
                    this.translate.instant('LABEL.KO_CFG'),
                    this.translate.instant('LABEL.ErroreSalvataggioMacrofase')
                );
            }
        });
    }




    openCategoryDialog(data?: any, branch?: any): void {
        // console.log('data:', data);

        if (data) {
            this.selectedCategory = {
                ...data,
                percIncrementoFestivoRif: Math.round((data.percIncrementoFestivoRif - 1) * 100),
                percIncrementoNotturnoRif: Math.round((data.percIncrementoNotturnoRif - 1) * 100),
                percIncrementoTrasfertaRif: Math.round((data.percIncrementoTrasfertaRif - 1) * 100)
            };
            this.currentNode = this.findNodeById(data.id);

        } else {
            // Nuova categoria
            this.selectedCategory = {
                name: '',
                code: '',
                percIncrementoFestivoRif: 0,
                percIncrementoNotturnoRif: 0,
                percIncrementoTrasfertaRif: 0,
                isActive: true,
                isVisible: true,
                codCountry: branch?.codCountry,
                codLanguage: branch?.codLanguage,
                branchId: branch?.id ?? null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            this.percIncrementoFestivoRif = 0;
            this.percIncrementoNotturnoRif = 0;
            this.percIncrementoTrasfertaRif = 0;
            this.currentNode = this.findNodeById(branch?.id);
        }
    }


    onCategoryDialogClose(): void {
        this.selectedCategory = null;
    }

    onCategoryDialogSave(): void {
        const isEdit = !!this.selectedCategory.id;

        const transformedBase = {
            ...this.selectedCategory,
            percIncrementoFestivoRif: Number(((this.selectedCategory.percIncrementoFestivoRif / 100) + 1).toFixed(4)),
            percIncrementoNotturnoRif: Number(((this.selectedCategory.percIncrementoNotturnoRif / 100) + 1).toFixed(4)),
            percIncrementoTrasfertaRif: Number(((this.selectedCategory.percIncrementoTrasfertaRif / 100) + 1).toFixed(4))
        };

        const payload = this.withUpdatedBy(transformedBase);

        const save$ = isEdit
            ? this.cfgService.updateCategory(payload.id, payload)
            : this.cfgService.createCategory(payload);

        this.isLoading = true;

        save$.subscribe({
            next: (saved) => {
                // ID certo della categoria (sia create che update)
                const targetId = saved?.id ?? payload.id;

                this.selectedCategory = null;

                // Riapre il ramo fino alla categoria
                this.restoreAndUpdateTreeAfterSave(targetId, 'Categoria');

                this.toastr.success(
                    this.translate.instant('LABEL.OK_CFG'),
                    this.translate.instant('LABEL.CategoriaSalvataConSuccesso')
                );
            },
            error: (err) => {
                console.error('Errore salvataggio categoria:', err);
                this.toastr.error(
                    this.translate.instant('LABEL.ErroreSalvataggio'),
                    this.translate.instant('LABEL.Errore')
                );
                this.isLoading = false;
            }
        });
    }



    saveOpenedPath(node: any) {
        const path: string[] = [];

        if (node?.data) {
            while (node && node.data) {
                // console.log('➡️ Salgo su (D3):', node.data.name);
                path.unshift(node.data.name);
                node = node.parent;
            }
        } else if (node?.name) {
            // console.log('➡️ Nodo semplice:', node.name);
            path.unshift(node.name);
        }

        this.openedNodePath = path;
        // console.log('✅ Path costruito:', path);
    }




    restoreOpenedPath() {
        let currentLevel = this.treeData;

        for (const name of this.openedNodePath) {
            const kids = (currentLevel.children || currentLevel._children || []);
            const target = kids.find(child => child.name === name);
            if (!target) {
                // LOG #3: se si ferma alla categoria, lo vediamo qui
                if (name.startsWith('Categoria:')) {
                    console.log('[CFG] restoreOpenedPath STOP su Categoria.',
                        'Cercavo:', name, 'Disponibili:', kids.map((c: any) => c.name)
                    );
                }
                break;
            }
            if (target._children) {
                target.children = target._children;
                delete target._children;
            }
            currentLevel = target;
        }
    }



    restoreAndUpdateTreeAfterSave(
        targetId?: number,
        targetType?: 'Fase' | 'Macrofase' | 'Categoria' | 'LOB',
        desiredNames?: {
            nationIso: string;
            branchName: string;
            categoryName: string;
            macroId: number;
            macroName: string;
        } | null,
        phaseName?: string // se targetType === 'Fase' passiamo il nome della fase duplicata
    ): void {
        // 0) prova a determinare SUBITO la nazione da desiredNames o dalla path aperta
        let nationCode = (
            desiredNames?.nationIso ||
            this.getNationCodeFromLabel(
                this.openedNodePath.find(n => typeof n === 'string' && n.startsWith('Nazione: '))
            ) ||
            ''
        ).toString().toUpperCase();

        forkJoin({
            branches: this.branchService.getAllBranches(),
            categories: this.categoryService.getAllCategories(),
            macroPhases: this.macroPhaseService.getAllMacroPhases(),
            phases: this.phaseService.getAllPhases(),
        }).subscribe({
            next: res => {
                // 🛡️ 1) se la nazione è ancora vuota, ricavala dal RECORD target (dataset grezzo non filtrato)
                if (!nationCode && targetId && targetType) {
                    switch (targetType) {
                        case 'Macrofase': {
                            const m = (res.macroPhases || []).find((x: any) => x.id === targetId);
                            nationCode = (m?.codCountry || '').toString().toUpperCase();
                            break;
                        }
                        case 'Fase': {
                            const p = (res.phases || []).find((x: any) => x.id === targetId);
                            const m = p ? (res.macroPhases || []).find((x: any) => x.id === p.macroPhaseId) : null;
                            nationCode = (p?.codCountry || m?.codCountry || '').toString().toUpperCase();
                            break;
                        }
                        case 'Categoria': {
                            const c = (res.categories || []).find((x: any) => x.id === targetId);
                            nationCode = (c?.codCountry || '').toString().toUpperCase();
                            break;
                        }
                        case 'LOB': {
                            const b = (res.branches || []).find((x: any) => x.id === targetId);
                            nationCode = (b?.codCountry || '').toString().toUpperCase();
                            break;
                        }
                    }
                }

                // 2) applica filtro PER NAZIONE solo se ho una nazione; altrimenti NON filtrare
                const applyCountryFilter = (arr: any[]) =>
                    nationCode
                        ? (arr || []).filter(x => String(x?.codCountry || '').toUpperCase() === nationCode)
                        : (arr || []);

                this.branches = applyCountryFilter(res.branches);
                this.categories = applyCountryFilter(res.categories);
                this.macroPhases = applyCountryFilter(res.macroPhases);
                this.phases = applyCountryFilter(res.phases);

                // 3) ricostruisci le nazioni e apri quella corretta
                this.buildTreeData();

                // trova la label della nazione (preferisci quella calcolata, fallback alla path già aperta)
                let nationLabel = '';
                if (nationCode) {
                    const nObj = this.countries.find(c => c.id === nationCode);
                    if (nObj) nationLabel = `Nazione: ${nObj.name}`;
                }
                if (!nationLabel) {
                    nationLabel = this.openedNodePath.find(n => typeof n === 'string' && n.startsWith('Nazione: ')) || '';
                }

                const nationNode = (this.treeData.children || []).find(n => n.name === nationLabel);
                if (nationNode) {
                    // se nationCode è ancora vuoto, prova a usare il code del nodo (è l’ISO)
                    const iso = nationCode || (nationNode as any).code || '';
                    nationNode.children = this.buildChildrenForNation(iso);
                    delete (nationNode as any)._children;
                    console.log(
                        '[CFG] Nazione aperta:',
                        nationLabel,
                        'LOB:',
                        (nationNode.children || []).map((c: any) => c.name)
                    );
                }

                // 4) prova prima la path "per nomi" con guard sul parent (se disponibile)
                let targetPath: string[] | null = null;
                if (desiredNames) {
                    const p = this.buildPathByNamesAndMacroGuard({
                        ...desiredNames,
                        phaseName: targetType === 'Fase' ? phaseName : undefined
                    });
                    if (p && p.length) targetPath = p;
                }

                // 5) fallback robusto per id/type
                if (!targetPath && targetId && targetType) {
                    targetPath = this.findPathToNodeByIdAndType(targetId, targetType);
                }

                if (targetPath && targetPath.length) {
                    this.openedNodePath = targetPath.slice();
                }

                this.restoreOpenedPath();
                this.renderTree();
                this.isLoading = false;

                // highlight + scroll sul target
                if (targetPath && targetPath.length) {
                    const path = targetPath.slice();
                    setTimeout(() => {
                        this.flashNodeByPath(path);
                        this.scrollToNodeByPath(path);
                    }, 0);
                }
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }




    private findNodeByIdAndType(id: number, type: string): any {
        let foundNode: any = null;

        function search(node: any) {
            if (node.originalData?.id === id && node.type === type) {
                foundNode = node;
                return;
            }
            (node.children || []).forEach(search);
            (node._children || []).forEach(search);
        }

        search(this.treeData);
        return foundNode;
    }




    findNodeById(id: number): any {
        const root = d3.hierarchy(this.treeData);
        let found: any = null;

        root.each((node: any) => {
            if (node.data.id === id) {
                found = node;
            }
        });

        return found;
    }



    getNationCodeFromLabel(label?: string): string {
        if (!label) return '';
        const parts = label.split(':').map(s => s.trim());
        const countryName = parts[1] ?? '';
        const match = this.countries.find(c => c.name.toLowerCase() === countryName.toLowerCase());
        return match?.id || '';
    }


    generateUniquePhaseCode(): string {
        let base = 'F0600001';
        let usedCodes = this.phases.map(p => p.code);
        let counter = 1;

        while (usedCodes.includes(base)) {
            counter++;
            base = 'F06' + counter.toString().padStart(5, '0');
        }

        return base;
    }
    generateUniqueMacroPhaseCode(): string {
        let base = 'MP1';
        let usedCodes = this.macroPhases.map(m => m.code);
        let counter = 1;

        while (usedCodes.includes(base)) {
            counter++;
            base = 'MP' + counter;
        }

        return base;
    }

    openSearchModal() {
        this.searchQuery = '';
        this.searchResults = [];
        this.showSearchModal = true;
    }
    closeSearchModal() {
        this.showSearchModal = false;
    }
    trackByResult = (_: number, r: SearchResult) =>
        `${r.type}-${r.nationIso}-${r.ids.branchId ?? 0}-${r.ids.categoryId ?? 0}-${r.ids.macroId ?? 0}-${r.ids.phaseId ?? 0}`;

    performSearch() {
        const oldPath = this.openedNodePath?.slice() ?? [];
        if (oldPath.length) {
            this.collapsePathToNation(oldPath);
            this.renderTree();
        }
        const q = this.normalize(this.searchQuery);
        if (!q) { this.searchResults = []; return; }

        this.searchLoading = true;

        forkJoin({
            branches: this.branchService.getAllBranches(),
            categories: this.categoryService.getAllCategories(),
            macroPhases: this.macroPhaseService.getAllMacroPhases(),
            phases: this.phaseService.getAllPhases(),
        }).subscribe({
            next: res => {
                const branches = Array.isArray(res.branches) ? res.branches : [];
                const categories = Array.isArray(res.categories) ? res.categories : [];
                const macros = Array.isArray(res.macroPhases) ? res.macroPhases : [];
                const phases = Array.isArray(res.phases) ? res.phases : [];

                const catById = new Map(categories.map(c => [c.id, c]));
                const macroById = new Map(macros.map(m => [m.id, m]));
                const phaseById = new Map(phases.map(p => [p.id, p]));

                const results: SearchResult[] = [];

                const nationNameByIso = new Map(this.countries.map(c => [c.id, c.name]));

                const includesQ = (s: any) => this.normalize(String(s || '')).includes(q);

                for (const br of branches) {
                    const nationIso = String(br.codCountry || '').toUpperCase();
                    const nationName = nationNameByIso.get(nationIso) || nationIso;

                    // LOB match
                    if (includesQ(br.name)) {
                        results.push({
                            nationIso, nationName, type: 'LOB',
                            ids: { branchId: br.id },
                            labels: { branchName: br.name },
                            pathNames: [`Nazione: ${nationName}`, br.name]
                        });
                    }

                    // categorie collegate alla branch
                    const relCats = (br.BranchesCategoriesRel || [])
                        .map((rel: any) => catById.get(rel.BranchesCategories?.CategoryId))
                        .filter(Boolean);

                    for (const cat of relCats) {
                        if (includesQ(cat.name)) {
                            results.push({
                                nationIso, nationName, type: 'Categoria',
                                ids: { branchId: br.id, categoryId: cat.id },
                                labels: { branchName: br.name, categoryName: cat.name },
                                pathNames: [`Nazione: ${nationName}`, br.name, `Categoria: ${cat.name}`]
                            });
                        }

                        // macrofasi collegate alla categoria
                        const relMacros = (cat.CategoriesMacroPhasesRel || [])
                            .map((rel: any) => macroById.get(rel.CategoriesMacroPhases?.MacroPhaseId || rel.id))
                            .filter(Boolean);

                        for (const macro of relMacros) {
                            if (includesQ(macro.name)) {
                                results.push({
                                    nationIso, nationName, type: 'Macrofase',
                                    ids: { branchId: br.id, categoryId: cat.id, macroId: macro.id },
                                    labels: { branchName: br.name, categoryName: cat.name, macroName: macro.name },
                                    pathNames: [`Nazione: ${nationName}`, br.name, `Categoria: ${cat.name}`, `Macrofase: ${macro.name}`]
                                });
                            }

                            // fasi collegate alla macrofase
                            const relPhases = Array.isArray(macro.MacroPhasesPhasesRel)
                                ? macro.MacroPhasesPhasesRel.map((rel: any) => phaseById.get(rel.id)).filter(Boolean)
                                : [];

                            for (const ph of relPhases) {
                                if (includesQ(ph.name)) {
                                    results.push({
                                        nationIso, nationName, type: 'Fase',
                                        ids: { branchId: br.id, categoryId: cat.id, macroId: macro.id, phaseId: ph.id },
                                        labels: { branchName: br.name, categoryName: cat.name, macroName: macro.name, phaseName: ph.name },
                                        pathNames: [
                                            `Nazione: ${nationName}`, br.name,
                                            `Categoria: ${cat.name}`, `Macrofase: ${macro.name}`, `Fase: ${ph.name}`
                                        ]
                                    });
                                }
                            }
                        }
                    }
                }

                // ordina: prima match più profondi, poi alfabetico breadcrumb
                results.sort((a, b) => {
                    const depthA = a.pathNames.length, depthB = b.pathNames.length;
                    if (depthA !== depthB) return depthB - depthA;
                    return a.pathNames.join(' → ').localeCompare(b.pathNames.join(' → '));
                });

                // opzionale: limita a 200
                this.searchResults = results.slice(0, 200);
                this.searchLoading = false;
            },
            error: _ => {
                this.searchLoading = false;
                this.searchResults = [];
            }
        });
    }

    private normalize(s: string) {
        return (s || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, ''); // rimuove accenti
    }
    navigateToResult(r: SearchResult) {
        this.showSearchModal = false;
        const oldPath = this.openedNodePath?.slice() ?? [];
        if (oldPath.length) {
            this.collapsePathToNation(oldPath);
            this.renderTree();
        }

        // 1) prepara la path da aprire
        this.openedNodePath = r.pathNames.slice(); // es. ["Nazione: Italia", "BUILDINGS", "Categoria: ...", ...]

        // 2) assicurati di avere i dati della nazione e popola i figli della Nazione
        const nationLabel = `Nazione: ${r.nationName}`;
        const nationNode = (this.treeData.children || []).find(n => n.name === nationLabel);

        if (!nationNode) {
            // se per qualche motivo non esiste, ricrea le nazioni
            this.buildTreeData();
        }

        // carica le liste filtrate per nation, popola figli e apri path
        this.isLoading = true;
        forkJoin({
            branches: this.branchService.getAllBranches(),
            categories: this.categoryService.getAllCategories(),
            macroPhases: this.macroPhaseService.getAllMacroPhases(),
            phases: this.phaseService.getAllPhases(),
        }).subscribe({
            next: res => {
                // filtra per nazione
                this.branches = this.filterByCountry(res.branches || [], r.nationIso);
                this.categories = this.filterByCountry(res.categories || [], r.nationIso);
                this.macroPhases = this.filterByCountry(res.macroPhases || [], r.nationIso);
                this.phases = this.filterByCountry(res.phases || [], r.nationIso);

                // costruisci i figli della nazione e aprila
                const nn = (this.treeData.children || []).find(n => n.name === nationLabel);
                if (nn) {
                    nn.children = this.buildChildrenForNation(r.nationIso);
                    delete (nn as any)._children;
                }

                // ripristina/apri path e renderizza
                this.restoreOpenedPath();
                this.renderTree();
                this.isLoading = false;

                // highlight + scroll
                this.flashNodeByPath(r.pathNames);
                this.scrollToNodeByPath(r.pathNames);
            },
            error: _ => { this.isLoading = false; }
        });
    }
    private flashNodeByPath(path: string[]) {
        const nodeData = this.findNodeDataByPath(path);
        if (!nodeData) return;
        (nodeData as any).__flash = true;
        this.renderTree(); // applica stile
        setTimeout(() => {
            (nodeData as any).__flash = false;
            this.renderTree();
        }, 3200);
    }

    private findNodeDataByPath(path: string[]): NodeData | null {
        let curr: any = this.treeData;
        for (const name of path) {
            const next = (curr.children || curr._children || []).find((c: any) => c.name === name);
            if (!next) return null;
            curr = next;
        }
        return curr;
    }
    private scrollToNodeByPath(path: string[]) {
        // ricostruisci layout come in renderTree per ottenere x,y del nodo
        const root = d3.hierarchy(this.treeData);
        const treeLayout = d3.tree<NodeData>().nodeSize([120, 200]);
        const tree = treeLayout(root);

        // calcola offset di colonna come in renderTree
        this.computeDepthOffsets(tree);
        tree.descendants().forEach(n => { n.y = this.depthOffsets[n.depth]; });

        // trova il nodo target
        let curr = tree as any;
        for (const name of path) {
            const next = (curr.children || []).find((c: any) => c.data.name === name);
            if (!next) return;
            curr = next;
        }

        const n = curr as d3.HierarchyPointNode<NodeData>;
        // stessi margini di renderTree
        const margin = { top: 100, right: 120, bottom: 40, left: 100 };
        const padding = 40;
        const offsetY = margin.top + padding - (d3.extent(tree.descendants(), d => d.x) as [number, number])[0];

        const absX = (margin.left + padding + n.y) * this.zoomLevel;
        const absY = (offsetY + n.x) * this.zoomLevel;

        const container = this.diagramRef.nativeElement as HTMLElement;
        container.scrollTo({
            left: Math.max(0, absX - container.clientWidth / 2),
            top: Math.max(0, absY - container.clientHeight / 2),
            behavior: 'smooth'
        });
    }

    private collapsePathToNation(path: string[]): void {
        if (!path || !path.length) return;

        // Trova l’indice del segmento "Nazione: ..."
        const nationIdx = path.findIndex(n => typeof n === 'string' && n.startsWith('Nazione: '));
        if (nationIdx === -1) return; // se non c’è, non faccio nulla

        // Naviga fino al nodo Nazione indicato dalla path
        let curr: any = this.treeData;
        for (let i = 0; i <= nationIdx; i++) {
            const name = path[i];
            const next = (curr.children || curr._children || []).find((c: any) => c.name === name);
            if (!next) return; // path non coerente con l’albero attuale
            curr = next;
        }

        // curr è il nodo Nazione: collassa i suoi figli (chiude tutto quel ramo)
        if (curr.children && curr.children.length) {
            curr._children = curr.children;
            delete curr.children;
        }
    }


}
